import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import {
  CartesianGrid,
  Line,
  LineChart,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

const toDisplayDate = (value) => {
  if (!value) return 'TBD';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateLabel = (dateString) => {
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-US', { year: '2-digit', month: 'short' });
};

const unitFormatter = (unit, value) => {
  if (!Number.isFinite(value)) return 'N/A';
  if (unit === 'percent') return `${value.toFixed(1)}%`;
  if (unit === 'thousands') return `${value.toLocaleString('en-US')}k`;
  return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
};

const percentChangeFormatter = (value) => {
  if (!Number.isFinite(value)) return 'N/A';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

// Plain-language explanations shown in an (i) tooltip on each chart card.
const INDICATOR_DESCRIPTIONS = {
  cpi_all_items:
    'Consumer Price Index (CPI): measures the average change in prices that U.S. consumers pay for a basket of everyday goods and services (food, housing, gas, clothing, medical care, etc.). It is the most-watched gauge of inflation. A rising CPI means the cost of living is going up.',
  ppi_final_demand:
    'Producer Price Index (PPI): measures the average change in prices that U.S. producers receive for their output \u2014 essentially wholesale / factory-gate prices. PPI moves before CPI, so it is often used as an early signal of consumer inflation pressure down the road.',
  unemployment_rate:
    'Unemployment Rate: the share of people in the labor force who are actively looking for work but cannot find a job. Lower is generally better for the economy; sharp increases often signal a slowdown or recession.',
  nonfarm_payrolls:
    'Nonfarm Payrolls: the total number of paid U.S. workers excluding farm workers, government, private households, and non-profit employees. The monthly change is the headline jobs number markets react to on the first Friday of every month.',
  jolts_job_openings:
    'JOLTS Job Openings: estimated number of unfilled positions employers are actively recruiting for. High openings suggest stronger labor demand; sustained declines can indicate cooling hiring demand.',
  jolts_hires:
    'JOLTS Hires: estimated number of workers added to payrolls during the month. This tracks the pace of labor market matching and complements payroll growth data.',
  jolts_quits:
    'JOLTS Quits: workers voluntarily leaving jobs. Quits often rise when workers feel confident about finding better opportunities and tend to fall when the labor market weakens.',
  jolts_total_separations:
    'JOLTS Total Separations: total workers leaving employment (quits + layoffs/discharges + other separations). Useful for monitoring churn and labor market stress.',
};

const EconomicCalendar = () => {
  const theme = useTheme();
  const [daysAhead, setDaysAhead] = useState(45);
  const [loading, setLoading] = useState(true);
  const [loadingSections, setLoadingSections] = useState({
    calendar: true,
    releases: true,
    indicators: true,
  });
  const [error, setError] = useState('');
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [latestReleases, setLatestReleases] = useState([]);
  const [indicatorSeries, setIndicatorSeries] = useState([]);
  const [zoomYears, setZoomYears] = useState(3);
  // 'sa' = seasonally adjusted, 'nsa' = not seasonally adjusted.
  // Only applies to indicators that expose a `variants` object on the API payload
  // (currently CPI, PPI, and Nonfarm Payrolls).
  const [seasonalAdjustment, setSeasonalAdjustment] = useState('sa');

  useEffect(() => {
    const controller = new AbortController();

    const loadEconomicData = async () => {
      setLoading(true);
      setError('');
      setLoadingSections({ calendar: true, releases: true, indicators: true });

      const fetchJson = async (url, section, sectionErrorPrefix) => {
        try {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) {
            throw new Error(`${sectionErrorPrefix} (HTTP ${response.status})`);
          }
          return await response.json();
        } finally {
          if (!controller.signal.aborted) {
            setLoadingSections((prev) => ({ ...prev, [section]: false }));
          }
        }
      };

      try {
        const calendarPromise = fetchJson(
          `${API_BASE_URL}/api/economic/calendar?days=${daysAhead}&limit=120`,
          'calendar',
          'Calendar request failed'
        );

        const releasesPromise = fetchJson(
          `${API_BASE_URL}/api/economic/latest-releases?limit=12`,
          'releases',
          'Latest releases request failed'
        )
          .then((releasesPayload) => {
            if (!controller.signal.aborted) {
              setLatestReleases(Array.isArray(releasesPayload.releases) ? releasesPayload.releases : []);
            }
          })
          .catch((err) => {
            if (err.name === 'AbortError' || controller.signal.aborted) return;
            console.warn('Latest releases request failed:', err.message);
            setLatestReleases([]);
          });

        const indicatorsPromise = fetchJson(
          `${API_BASE_URL}/api/economic/indicators?years=10`,
          'indicators',
          'Indicators request failed'
        )
          .then((indicatorsPayload) => {
            if (!controller.signal.aborted) {
              setIndicatorSeries(Array.isArray(indicatorsPayload.indicators) ? indicatorsPayload.indicators : []);
            }
          })
          .catch((err) => {
            if (err.name === 'AbortError' || controller.signal.aborted) return;
            console.warn('Indicators request failed:', err.message);
            setIndicatorSeries([]);
          });

        const calendarPayload = await calendarPromise;

        setCalendarEvents(Array.isArray(calendarPayload.events) ? calendarPayload.events : []);

        // Secondary sections continue loading independently; do not block tab render.
        void Promise.allSettled([releasesPromise, indicatorsPromise]);
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError(err.message || 'Failed to load economic data');
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadEconomicData();

    return () => controller.abort();
  }, [daysAhead]);

  const upcomingEvents = useMemo(() => calendarEvents.slice(0, 40), [calendarEvents]);

  const indicatorCharts = useMemo(() => {
    return indicatorSeries
      .filter((indicator) => Array.isArray(indicator?.points) && indicator.points.length > 0)
      .map((indicator) => {
        const allPoints = indicator.points.map((point) => ({
          ...point,
          label: formatDateLabel(point.date),
        }));

        const latestDate = allPoints.length > 0 ? new Date(`${allPoints[allPoints.length - 1].date}T00:00:00`) : null;
        const cutoffDate = latestDate && Number.isFinite(latestDate.getTime())
          ? new Date(latestDate.getFullYear() - zoomYears, latestDate.getMonth(), 1)
          : null;

        const points = cutoffDate
          ? allPoints.filter((pt) => {
            const pointDate = new Date(`${pt.date}T00:00:00`);
            return Number.isFinite(pointDate.getTime()) && pointDate >= cutoffDate;
          })
          : allPoints;

        const displayPoints = points.length > 1 ? points : allPoints;
        const latestPoint = displayPoints[displayPoints.length - 1] || null;
        const prevPoint = displayPoints.length > 1 ? displayPoints[displayPoints.length - 2] : null;
        const defaultPercentChange =
          latestPoint && prevPoint && Number.isFinite(prevPoint.value) && prevPoint.value !== 0
            ? ((latestPoint.value - prevPoint.value) / prevPoint.value) * 100
            : null;

        // If the indicator exposes seasonal variants (SA + NSA series), compute the
        // percent-change figure from the user-selected adjustment. The line chart
        // itself continues to use the primary `points` series.
        let percentChange = defaultPercentChange;
        let percentChangeSource = null; // 'sa' | 'nsa' | null
        if (indicator.variants && indicator.variants[seasonalAdjustment]) {
          const variant = indicator.variants[seasonalAdjustment];
          const variantPoints = Array.isArray(variant?.points) ? variant.points : [];
          if (variantPoints.length >= 2) {
            const v1 = variantPoints[variantPoints.length - 1];
            const v0 = variantPoints[variantPoints.length - 2];
            if (Number.isFinite(v0?.value) && v0.value !== 0 && Number.isFinite(v1?.value)) {
              percentChange = ((v1.value - v0.value) / v0.value) * 100;
              percentChangeSource = seasonalAdjustment;
            }
          }
        }

        // Find if any point is preliminary (scraped)
        const hasPreliminary = displayPoints.some((pt) => pt.preliminary === true);

        const shouldUseTightYAxis = ['cpi_all_items', 'ppi_final_demand', 'nonfarm_payrolls'].includes(indicator.key);
        let yDomain = ['auto', 'auto'];
        if (shouldUseTightYAxis) {
          const values = displayPoints.map((pt) => Number(pt.value)).filter((v) => Number.isFinite(v));
          if (values.length > 1) {
            const minVal = Math.min(...values);
            const maxVal = Math.max(...values);
            const span = maxVal - minVal;
            const pad = span > 0 ? span * 0.12 : Math.max(Math.abs(maxVal) * 0.02, 1);
            yDomain = [Math.max(0, minVal - pad), maxVal + pad];
          }
        }

        return {
          ...indicator,
          points: displayPoints,
          latestPoint,
          percentChange,
          percentChangeSource,
          hasVariants: Boolean(indicator.variants),
          hasPreliminary,
          yDomain,
        };
      });
  }, [indicatorSeries, zoomYears, seasonalAdjustment]);

  const lineColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load BLS economic calendar data: {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {/* Legend for preliminary vs official data */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <svg width="18" height="18" style={{ verticalAlign: 'middle' }}>
            <circle cx="9" cy="9" r="7" fill={theme.palette.warning.main} stroke="#fff" strokeWidth="2" />
          </svg>
          <Typography variant="body2" color="text.secondary">
            Preliminary (scraped/fallback)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <svg width="18" height="18" style={{ verticalAlign: 'middle' }}>
            <circle cx="9" cy="9" r="7" fill={theme.palette.primary.main} stroke="#fff" strokeWidth="2" />
          </svg>
          <Typography variant="body2" color="text.secondary">
            Official (BLS API)
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
        <Typography variant="body2" color="text.secondary">
          <b>Prelim</b> label means the latest value is preliminary (from FRED or scraped from BLS site, not yet in official API).
        </Typography>
      </Box>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          BLS Macro Indicators (Actual Data)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Direct time-series values from the BLS API for CPI, PPI, unemployment, and payrolls.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            % change basis (CPI / PPI / Payrolls):
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={seasonalAdjustment}
            onChange={(_, next) => {
              if (next !== null) setSeasonalAdjustment(next);
            }}
          >
            <ToggleButton value="sa">Seasonally Adjusted</ToggleButton>
            <ToggleButton value="nsa">Unadjusted</ToggleButton>
          </ToggleButtonGroup>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={zoomYears}
            onChange={(_, nextZoom) => {
              if (nextZoom !== null) setZoomYears(nextZoom);
            }}
          >
            <ToggleButton value={1}>1Y</ToggleButton>
            <ToggleButton value={2}>2Y</ToggleButton>
            <ToggleButton value={3}>3Y</ToggleButton>
            <ToggleButton value={5}>5Y</ToggleButton>
            <ToggleButton value={10}>10Y</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 2,
          }}
        >
          {indicatorCharts.map((indicator, index) => (
            <Box key={indicator.key} sx={{ minHeight: 320, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {indicator.label}
                  </Typography>
                  {INDICATOR_DESCRIPTIONS[indicator.key] && (
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.45 }}>
                          {INDICATOR_DESCRIPTIONS[indicator.key]}
                        </Typography>
                      }
                      componentsProps={{ tooltip: { sx: { maxWidth: 340, p: 1.25 } } }}
                    >
                      <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} aria-label={`About ${indicator.label}`}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {indicator.hasPreliminary && (
                    <Chip size="small" color="warning" label="Prelim" title="Latest value is preliminary (scraped from BLS site)" />
                  )}
                  <Chip
                    size="small"
                    variant="outlined"
                    color={indicator.hasVariants ? (seasonalAdjustment === 'sa' ? 'primary' : 'default') : 'default'}
                    label={
                      indicator.hasVariants && indicator.percentChangeSource
                        ? `${percentChangeFormatter(indicator.percentChange)} ${indicator.percentChangeSource.toUpperCase()}`
                        : percentChangeFormatter(indicator.percentChange)
                    }
                    title={
                      indicator.hasVariants
                        ? `Month-over-month change (${seasonalAdjustment === 'sa' ? 'Seasonally Adjusted' : 'Not Seasonally Adjusted'})`
                        : 'Change vs previous data point'
                    }
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={indicator.latestPoint ? unitFormatter(indicator.unit, indicator.latestPoint.value) : 'N/A'}
                  />
                </Box>
              </Box>
              <Box sx={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={indicator.points} margin={{ top: 8, right: 12, left: 2, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: theme.palette.mode === 'dark' ? '#fff' : '#222' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: theme.palette.mode === 'dark' ? '#fff' : '#222' }}
                      width={64}
                      domain={indicator.yDomain}
                      allowDataOverflow
                    />
                    <Legend />
                    <RechartsTooltip
                      cursor={{ stroke: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.6)' : 'rgba(71, 85, 105, 0.35)' }}
                      content={({ active, payload }) => {
                        if (!active || !Array.isArray(payload) || payload.length === 0) return null;
                        const point = payload[0]?.payload || {};
                        const prelim = point.preliminary === true;

                        return (
                          <Box
                            sx={{
                              backgroundColor: theme.palette.mode === 'dark' ? '#101826' : '#ffffff',
                              border: '1px solid',
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.35)' : 'rgba(15, 23, 42, 0.12)',
                              borderRadius: 2,
                              boxShadow: theme.palette.mode === 'dark' ? '0 10px 28px rgba(2, 6, 23, 0.6)' : '0 8px 20px rgba(15, 23, 42, 0.12)',
                              px: 2,
                              py: 1.5,
                              minWidth: 280,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.mode === 'dark' ? 'rgba(226, 232, 240, 0.8)' : 'text.secondary',
                                fontSize: 16,
                                fontWeight: 600,
                                mb: 0.75,
                              }}
                            >
                              {point.date || point.label || '--'}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: lineColors[index % lineColors.length],
                                fontWeight: 700,
                                fontSize: 14,
                              }}
                            >
                              {prelim ? `${indicator.label} (Prelim)` : indicator.label} : {unitFormatter(indicator.unit, Number(point.value))}
                            </Typography>
                          </Box>
                        );
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      name={indicator.label}
                      stroke={lineColors[index % lineColors.length]}
                      strokeWidth={2}
                      dot={({ cx, cy, payload }) =>
                        payload.preliminary ? (
                          <circle cx={cx} cy={cy} r={6} fill={theme.palette.warning.main} stroke="#fff" strokeWidth={2} />
                        ) : null
                      }
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          ))}
          {!loadingSections.indicators && indicatorCharts.length === 0 && (
            <Alert severity="warning">No indicator series returned from BLS right now.</Alert>
          )}
          {loadingSections.indicators && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              U.S. Economic Calendar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Source: U.S. Bureau of Labor Statistics (BLS). Times are Eastern Time.
            </Typography>
          </Box>

          <TextField
            label="Days Ahead"
            size="small"
            type="number"
            value={daysAhead}
            onChange={(e) => {
              const nextVal = Number.parseInt(e.target.value, 10);
              if (!Number.isFinite(nextVal)) return;
              setDaysAhead(Math.max(7, Math.min(365, nextVal)));
            }}
            sx={{ width: 140 }}
            inputProps={{ min: 7, max: 365 }}
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Time (ET)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Release</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {upcomingEvents.map((event) => (
                <TableRow key={event.id} hover>
                  <TableCell>{toDisplayDate(event.date)}</TableCell>
                  <TableCell>{event.timeEt || 'TBD'}</TableCell>
                  <TableCell>
                    {event.url ? (
                      <Link href={event.url} target="_blank" rel="noopener noreferrer" underline="hover">
                        {event.title}
                      </Link>
                    ) : (
                      event.title
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loadingSections.calendar && upcomingEvents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No scheduled events found.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {loadingSections.calendar && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography variant="h6" fontWeight={700}>
            Latest BLS Releases
          </Typography>
          <Chip size="small" label={`${latestReleases.length} items`} variant="outlined" />
        </Box>
        <Divider sx={{ mb: 1 }} />

        <List dense disablePadding>
          {latestReleases.map((release) => (
            <ListItem key={`${release.title}-${release.date}`} sx={{ px: 0 }}>
              <ListItemText
                primary={
                  release.url ? (
                    <Link href={release.url} target="_blank" rel="noopener noreferrer" underline="hover">
                      {release.title}
                    </Link>
                  ) : (
                    release.title
                  )
                }
                secondary={release.date || 'Date unavailable'}
              />
            </ListItem>
          ))}
          {!loadingSections.releases && latestReleases.length === 0 && (
            <ListItem sx={{ px: 0 }}>
              <ListItemText primary="No recent release data returned." />
            </ListItem>
          )}
          {loadingSections.releases && (
            <ListItem sx={{ px: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            </ListItem>
          )}
        </List>
      </Paper>
    </Box>
  );
};

export default EconomicCalendar;
